import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListChecks, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAiJob, isStopped } from "@/lib/aiJob";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const btn = "text-xs px-3 py-1.5 rounded-full border border-white/10 text-neutral-300 hover:bg-white/5 flex items-center gap-1.5 shrink-0 disabled:opacity-50";

function QuizMeButton({ chatId }) {
  const navigate = useNavigate();
  const { refreshEnt } = useAuth();
  const [busy, setBusy] = useState(false);
  const job = useAiJob({ onStopped: refreshEnt });
  const go = async () => {
    if (busy) { job.stop(); setBusy(false); return; }
    setBusy(true);
    try {
      const { data } = await job.run((o) => http.post(`/chats/${chatId}/quiz`, null, o));
      refreshEnt();
      navigate("/app/practice", { state: { set: data } });
    } catch (e) { if (!isStopped(e) && e.response?.status !== 402) toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <button onClick={go} className={btn} data-testid="chat-quiz-me-btn">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListChecks className="h-3.5 w-3.5 text-emerald-300" />} {busy ? "Stop" : "Quiz me on this chat"}
    </button>
  );
}

function ShareDialog({ chatId, onClose }) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);
  const [busy, setBusy] = useState("");
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    http.get("/groups").then((r) => setGroups(r.data))
      .catch((e) => { if (e.response?.status !== 402) toast.error(formatError(e)); closeRef.current(); });
  }, []);
  const share = async (g) => {
    setBusy(g.class_id);
    try {
      await http.post(`/chats/${chatId}/share`, { class_id: g.class_id });
      toast.success(`Shared with ${g.name}`, { action: { label: "Open", onClick: () => navigate(`/app/groups/${g.class_id}`) } });
      onClose();
    } catch (e) { toast.error(formatError(e)); setBusy(""); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0E121C] border-white/10 max-w-sm" data-testid="share-chat-dialog">
        <DialogHeader><DialogTitle className="font-display">Share this chat with a study group</DialogTitle></DialogHeader>
        <p className="text-xs text-neutral-500">Members will see your questions and the tutor's answers as a note.</p>
        {!groups && <Loader2 className="h-4 w-4 animate-spin" />}
        {groups && !groups.length && <p className="text-sm text-neutral-400">You're not in any study groups yet.</p>}
        <div className="space-y-2">
          {groups?.map((g) => (
            <button key={g.class_id} onClick={() => share(g)} disabled={!!busy} data-testid={`share-chat-group-${g.class_id}`}
              className="w-full text-left px-4 py-3 rounded-xl border border-white/10 hover:bg-white/5 flex items-center justify-between text-sm">
              <span className="truncate">{g.name}</span>
              {busy === g.class_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="text-xs text-neutral-500">{g.member_count} members</span>}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ChatActions({ chatId, hasAnswers }) {
  const { ent } = useAuth();
  const [sharing, setSharing] = useState(false);
  if (!chatId || !hasAnswers) return null;
  const openShare = () => {
    if (ent && !ent.features.groups) { window.dispatchEvent(new CustomEvent("paywall", { detail: { code: "feature", feature: "groups", needs: "starter" } })); return; }
    setSharing(true);
  };
  return (
    <div className="border-b border-white/10 px-4 py-2 flex gap-2 overflow-x-auto scroll-thin" data-testid="chat-actions">
      <div className="max-w-3xl mx-auto w-full flex gap-2 justify-end">
        <QuizMeButton chatId={chatId} />
        <button onClick={openShare} className={btn} data-testid="chat-share-btn"><Share2 className="h-3.5 w-3.5 text-violet-300" /> Share to group</button>
      </div>
      {sharing && <ShareDialog chatId={chatId} onClose={() => setSharing(false)} />}
    </div>
  );
}
